

























import { Component, Vue, Watch } from "vue-property-decorator";
import ReservationsTableMobile from "@/components/ReservationsTableMobile.vue";
import GlobalFilterSensitiveMixin from "@/mixins/GlobalFilterSensitiveMixin";
import { ListModel } from "@/shared/models/listModel";
import {
  NavigationGuardNext,
  Route,
} from "node_modules/vue-router/types/router";
import { Getter } from "vuex-class";
import { DateTime } from "luxon";
import _ from "lodash";
import { GlobalFilterProperty } from "@/store/modules/globalFilters";
import { VOccupancy } from "@/shared/models/VOccupancy";
import { formatOrderbyArray } from "@/shared/sortHelperFunctions";
import {
  DashboardPeriodEnum,
  ReservationsGridDefaultFilterByEnum,
} from "@/shared/models/enums.type";
import { OccupancyKPIResult } from "@/components/dashboard/KPI/OccupancyKPI.vue";

@Component({
  components: {
    ReservationsTableMobile,
  },
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext) {
    next((vm: Vue) => {
      const that = vm as ReservationsMobile;
      that.endpoint = to.meta?.endpoint;
      that.name = to.name ?? "Reservations";
      that.completedFilter = to.meta?.completedFilter;
      that.showDatePicker = to.meta?.datePicker ?? true;
      that.options.sortBy =
        to.name === "Dashboard.Occupancy" ? ["propertyName"] : ["arrivalDate"];
      that.numberOfItems = to.name === "Dashboard.Occupancy" ? 1000 : 25;
    });
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
    if (!this.$refs.showDatePicker) {
      this.$store.dispatch("setShowDatePicker", true);
    }
    next();
  },
  mixins: [GlobalFilterSensitiveMixin],
})
export default class ReservationsMobile extends Vue {
  endpoint = "/reservations";
  name = "Reservations";
  completedFilter = 0;
  loading = false;
  showDatePicker = true;
  numberOfItems = 25;
  done = false;
  initialSortOrder: string[] = [];

  @Getter("reservations/dateFilterApplied") dateFilterApplied?: boolean;
  @Getter("appParams/reservationsGridFiltersByDate")
  reservationsGridFiltersByDate?: number;

  @Getter("calendar/currentDay") currentDay?: DateTime;

  @Watch("currentDay", { immediate: true })
  currentChanged(fresh: DateTime) {
    if (fresh) {
      this.resetLoadMore();
      this.debouncedLoad(new Date(fresh.toISODate() ?? ""));
    }
  }

  @Watch("sensitiveKey", { immediate: true })
  sensitiveKeyChanged() {
    if (this.currentDay) {
      this.resetLoadMore();
      this.debouncedLoad(new Date(this.currentDay.toISODate() ?? ""));
    }
  }

  @Watch("name", { immediate: true })
  nameChanged() {
    if (this.currentDay) {
      this.debouncedLoad(new Date(this.currentDay.toISODate() ?? ""));
    }
  }

  @Watch("showDatePicker", { immediate: true })
  showDatePickerChanged(fresh: boolean) {
    this.showDatePicker = fresh;
    this.$store.dispatch("setShowDatePicker", fresh);
  }

  @Getter("globalFilters/filteredProperties")
  filteredProperties?: GlobalFilterProperty[];
  get fpNum() {
    return this.filteredProperties?.length ?? 0;
  }

  reservationFilters = {
    reservationId: "",
    arrivalOrDepartureDate: "",
    arrivalDateFromPeriod: "",
    arrivalDateToPeriod: "",
    departureDateFromPeriod: "",
    departureDateToPeriod: "",
    bookingDate: "",
    isFilteredByDate: null as boolean | null,
    optionExpirationDate: "",
    reservationHolderName: "",
    accomodationUnitName: "",
    statusId: null as number | null,
  };

  updateFilter(filters: any) {
    this.reservationFilters.reservationId = filters.reservationId;
    this.reservationFilters.arrivalOrDepartureDate =
      filters.arrivalOrDepartureDate;
    this.reservationFilters.arrivalDateFromPeriod =
      filters.arrivalDateFromPeriod;
    this.reservationFilters.arrivalDateToPeriod = filters.arrivalDateToPeriod;
    this.reservationFilters.departureDateFromPeriod =
      filters.departureDateFromPeriod;
    this.reservationFilters.departureDateToPeriod =
      filters.departureDateToPeriod;
    this.reservationFilters.bookingDate = filters.bookingDate;
    this.reservationFilters.isFilteredByDate = true;
    this.reservationFilters.optionExpirationDate = filters.optionExpirationDate;
    this.reservationFilters.reservationHolderName =
      filters.reservationHolderName;
    this.reservationFilters.accomodationUnitName = filters.accomodationUnitName;
    this.reservationFilters.statusId = filters.statusId;

    if (this.currentDay) {
      this.getReservations(new Date(this.currentDay.toISODate() ?? ""));
    }
  }

  sortOrder() {
    this.options.sortAsc[0] = !this.options.sortAsc[0];

    if (this.currentDay) {
      this.getReservations(new Date(this.currentDay.toISODate() ?? ""));
    }
  }

  created() {
    this.initialSortOrder =
      this.reservationsGridFiltersByDate ==
      ReservationsGridDefaultFilterByEnum.ArrivalDate
        ? ["arrivalDate"]
        : ["departureDate"];
  }

  sortByOrder(event: string) {
    this.options.sortBy[0] = event;

    if (event === "arrivalDate") {
      this.initialSortOrder = ["arrivalDate"];
    } else if (event === "departureDate") {
      this.initialSortOrder = ["departureDate"];
    } else if (event === "bookingDate") {
      this.initialSortOrder = ["bookingDate"];
    }

    if (this.currentDay) {
      this.getReservations(new Date(this.currentDay.toISODate() ?? ""));
    }
  }

  @Watch("dateFilterApplied")
  isDateFilterApplied() {
    if (this.currentDay) {
      this.getReservations(new Date(this.currentDay.toISODate() ?? ""));
    }
  }

  reservations: any[] = [];

  debouncedLoad = _.debounce(this.getReservations, 200);

  async getReservations(date: Date): Promise<void> {
    this.loading = true;
    if (!this.occupancy) {
      const res = await Vue.axios.get<ListModel<any>>(this.endpoint, {
        params: this.queryParams(date),
      });

      this.reservations = res.data.result;
      if (res.data.result.length < this.numberOfItems) {
        this.done = true;
      }
    } else {
      const res = await Vue.axios.get<OccupancyKPIResult>(this.endpoint, {
        params: this.queryParamsOccupancy(date),
      });
      this.reservations = res.data.items;
      this.reservations = this.transformItems(this.reservations);
      if (this.options.sortAsc[0]) {
        this.reservations.sort();
      } else {
        this.reservations.sort().reverse();
      }

      if (res.data.items.length < this.numberOfItems) {
        this.done = true;
      }
    }

    this.loading = false;
  }

  refreshMobileTable() {
    if (this.currentDay) {
      this.getReservations(new Date(this.currentDay.toISODate() ?? ""));
    }
  }

  options = {
    sortBy: ["arrivalDate"],
    sortAsc: [true],
  };

  queryParams(date: Date) {
    if (this.endpoint === "/reservations") {
      return {
        $skip: 0,
        $top: this.numberOfItems,
        $orderby: formatOrderbyArray(
          this.initialSortOrder,
          this.options.sortAsc.map((x) => x)
        ),
        $count: true,
        ...this.reservationFilters,
        isFilteredByDate: this.dateFilterApplied,
      };
    } else {
      return {
        $skip: 0,
        $top: this.occupancy ? 1000 : this.numberOfItems,
        $orderby: formatOrderbyArray(
          this.occupancy ? ["propertyName"] : this.options.sortBy,
          this.options.sortAsc
        ),
        $count: true,
        isCompletedFilter: this.completedFilter,
        filter: DashboardPeriodEnum.OnDate,
        date: date,
      };
    }
  }

  queryParamsOccupancy(date: Date) {
    return {
      $skip: 0,
      $top: this.occupancy ? 1000 : this.numberOfItems,
      $orderby: formatOrderbyArray(
        this.occupancy ? ["propertyName"] : this.options.sortBy,
        this.options.sortAsc
      ),
      $count: true,
      isCompletedFilter: this.completedFilter,
      filter: DashboardPeriodEnum.OnDate,
      from: date,
      to: date,
    };
  }

  get occupancy() {
    return this.name === "Dashboard.Occupancy";
  }

  async getMoreReservations() {
    if (this.currentDay && !this.loading && !this.done) {
      this.numberOfItems += 25;
      await this.getReservations(new Date(this.currentDay.toISODate() ?? ""));
    }
  }

  transformItems(items: VOccupancy[]) {
    if (this.fpNum > 1) {
      const grouped = _.groupBy(items, (x) => x.propertyId);
      return _.map(grouped, (x) =>
        x.reduce(
          (acc, i) => {
            return {
              ...acc,
              availableQuantity: acc.availableQuantity + i.availableQuantity,
              occupiedQuantity: acc.occupiedQuantity + i.occupiedQuantity,
              totalQuantity: acc.totalQuantity + i.totalQuantity,
              occupancyPercentage:
                (acc.totalQuantity + i.totalQuantity > 0
                  ? (acc.occupiedQuantity + i.occupiedQuantity) /
                    (acc.totalQuantity + i.totalQuantity)
                  : 1) * 100,
            };
          },
          {
            date: x[0].date,
            propertyId: x[0].propertyId,
            propertyName: x[0].propertyName,
            availableQuantity: 0,
            occupiedQuantity: 0,
            totalQuantity: 0,
            occupancyPercentage: 0,
          }
        )
      );
    } else {
      return items;
    }
  }

  resetLoadMore() {
    this.done = false;
    this.numberOfItems = 25;
  }
}
